import type { OptimizationApproach } from './optimizationApproach';

/** Default text for FINAL_STEP_PROMPT (connector, integrations, virtual user metaPrompts). */
export const DEFAULT_FINAL_STEP_PROMPT =
  'FINAL STEP: Answer the latest user message with one definitive reply, ' +
  'using all prior messages (including agents) as context. Keep it concise and resolve contradictions.';

/**
 * Ensures `metaPrompts` always includes a non-empty FINAL_STEP_PROMPT when merging
 * integration / connector / optimization payloads.
 */
export function mergeMetaPromptsWithFinalStepDefault(
  meta?: Record<string, string | undefined> | null
): Record<string, string> {
  const out: Record<string, string> = {};
  if (meta && typeof meta === 'object') {
    for (const [k, v] of Object.entries(meta)) {
      if (typeof v === 'string') out[k] = v;
    }
  }
  const fs = out.FINAL_STEP_PROMPT;
  if (typeof fs !== 'string' || fs.trim() === '') {
    out.FINAL_STEP_PROMPT = DEFAULT_FINAL_STEP_PROMPT;
  }
  return out;
}

/** Where to inject agent reminder text relative to the main prompt (backend / connector). */
export type ReminderPromptPosition = 'before' | 'after';

export function normalizeReminderPromptPosition(value: unknown): ReminderPromptPosition {
  return value === 'after' ? 'after' : 'before';
}

/** Normalize `rag_doc_ids` from API / legacy configs (omit string `"all"`). */
export function normalizeLegacyRagDocIds(value: unknown): string[] | undefined {
  if (value == null || value === '') return undefined;
  if (value === 'all') return undefined;
  if (Array.isArray(value)) {
    const ids = value.filter((x) => typeof x === 'string' && String(x).trim()).map((x) => String(x).trim());
    return ids.length ? ids : undefined;
  }
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return undefined;
}

/** Drop `rag_doc_ids` unless `use_rag` and a non-empty id list (avoid legacy `"all"` in JSON). */
export function stripRagDocIdsForApi<T extends Record<string, unknown>>(agent: T): T {
  const o = { ...agent } as Record<string, unknown>;
  const useRag = o.use_rag === true;
  const ids = o.rag_doc_ids;
  if (!useRag || !Array.isArray(ids) || ids.length === 0) {
    delete o.rag_doc_ids;
  }
  return o as T;
}

export type AgentCoordinatorState = {
  name: string;
  description: string;
  prompt: string;
  model: string;
  temperature: number;
  /** GPT-5 family: reasoning effort, sent as models_reasoning to the API */
  models_reasoning?: string;
  use_rag?: boolean;
  /**
   * When `use_rag` is true: restrict retrieval to these document ids (integration RAG).
   * Omit or leave empty to allow all indexed documents (do not send `"all"` in JSON).
   */
  rag_doc_ids?: string[];
  use_ltm?: boolean;
  use_diary_read?: boolean;
  use_diary_write?: boolean;
  use_portrait_read?: boolean;
  use_portrait_write?: boolean;
  /**
   * Whether this coordinator should be included into Training Job payload
   */
  is_trainable?: boolean;
  /** Backend agent id — always sent in fine-tuning payload (required by API) */
  agent_id?: string;
  /** Alternative field some APIs return for coordinator id */
  id?: string;
  /** Prepended or injected reminder for model output formatting (e.g. start with a fixed phrase) */
  reminder_prompt?: string;
  /** NLT admin: enable config patch tool for this coordinator */
  use_config_patch_tool?: boolean;
  /** NLT admin: list integration datasets */
  use_list_datasets_tool?: boolean;
  /** NLT admin: read dataset contents */
  use_read_dataset_tool?: boolean;
  /** NLT admin: list evaluators */
  use_list_evaluators_tool?: boolean;
  /** NLT admin: run evaluator */
  use_run_evaluator_tool?: boolean;
  /** NLT admin: propose patches to the workflow graph (POST /nlt/workflow-patch) */
  use_workflow_patch_tool?: boolean;
};

export type AgentWorker = {
  id: string;
  name: string;
  description: string;
  prompt: string;
  mode: 'synchronous' | 'asynchronous';
  model: string;
  temperature: number;
  models_reasoning?: string;
  use_rag?: boolean;
  /** Same semantics as coordinator `rag_doc_ids` when `use_rag` is true. */
  rag_doc_ids?: string[];
  use_ltm?: boolean;
  use_diary_read?: boolean;
  use_diary_write?: boolean;
  use_portrait_read?: boolean;
  use_portrait_write?: boolean;
  /**
   * Whether this worker should be included into Training Job payload
   */
  is_trainable?: boolean;
  reminder_prompt?: string;
  /** NLT admin: enable config patch tool for this worker */
  use_config_patch_tool?: boolean;
  /** NLT admin: list integration datasets */
  use_list_datasets_tool?: boolean;
  /** NLT admin: read dataset contents */
  use_read_dataset_tool?: boolean;
  /** NLT admin: list evaluators */
  use_list_evaluators_tool?: boolean;
  /** NLT admin: run evaluator */
  use_run_evaluator_tool?: boolean;
  /** NLT admin: propose patches to the workflow graph (POST /nlt/workflow-patch) */
  use_workflow_patch_tool?: boolean;
};

export type AgentConfiguratorState = {
  approach?: OptimizationApproach;
  coordinator: AgentCoordinatorState;
  workers: AgentWorker[];
  async_orchestrator?: {
    name?: string;
    model?: string;
    prompt?: string;
    enabled?: boolean;
    description?: string;
    temperature?: number;
    models_reasoning?: string;
    reminder_prompt?: string;
  };
  metaPrompts?: {
    CORE_PROMPT?: string;
    MASTER_SUFFIX?: string;
    ASYNC_SUFFIX?: string;
    SYNC_EXTRA?: string;
    RAG_PROMPT?: string;
    LTM_PROMPT?: string;
    MODERATION_PROMPT?: string;
    DIARY_WRITE_PROMPT?: string;
    DIARY_READ_PROMPT?: string;
    PORTRAIT_READ_PROMPT?: string;
    PORTRAIT_WRITE_PROMPT?: string;
    FINAL_STEP_PROMPT?: string;
    [key: string]: string | undefined;
  };
  /** Placement of reminder prompts for agents (default: before). */
  reminder_prompt_position?: ReminderPromptPosition;
};

